/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes, useEffect, useState, useMemo } from 'preact/compat';
import { classes, Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { events } from '@adobe-commerce/event-bus';
import { ProductModel } from '@/pdp/data/models';
import { isDateValid, toDateString } from '@/pdp/lib/date';
import { config, getFetchedProductData } from '@/pdp/api';

type DefaultSlotContext = {
  data: ProductModel | null;
};

export interface ProductAttributesProps extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
  slots?: {
    Attributes?: SlotProps<DefaultSlotContext>;
  };
}

export const ProductAttributes: Container<
  ProductAttributesProps,
  ProductModel | null
> = ({ initialData = null, scope, children, slots, ...props }) => {
  const DetailsLabel = useText('PDP.Product.Details.label').label;
  const { globalLocale = 'en-US' } = config.getConfig();

  const [locale, setLocale] = useState<string>(globalLocale);
  const [data, setData] = useState<ProductModel | null>(initialData);

  const defaultSlotContext: DefaultSlotContext = useMemo(
    () => ({ data }),
    [data]
  );

  // Listen for events
  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });
    const localeEvent = events.on('locale', setLocale);

    return () => {
      dataEvent?.off();
      localeEvent?.off();
    };
  }, [scope]);

  if (!data) return null;

  return (
    <div {...props} className={classes(['pdp-attributes', props.className])}>
      {DetailsLabel}
      {slots?.Attributes ? (
        <Slot
          name="Attributes"
          slot={slots?.Attributes}
          context={{ ...defaultSlotContext }}
        />
      ) : (
        <ul>
          {data?.attributes?.map(
            (
              { id, label, value }: { id: string; label: string; value: any },
              index: number
            ) => {
              if (id?.startsWith('ac_') || !value) {
                return null;
              }

              const __html = isDateValid(value)
                ? toDateString(value, locale)
                : value.toString();

              return (
                <li key={`attribute-${index}`}>
                  {/* eslint-disable-next-line react/no-danger */}
                  {label}: <span dangerouslySetInnerHTML={{ __html }} />
                </li>
              );
            }
          )}
        </ul>
      )}
    </div>
  );
};

ProductAttributes.getInitialData = getFetchedProductData;
